/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { HeaderSorterComponent } from '../../../common/tables/header-sorter/header-sorter.component';
import { DtcTableSearchComponent } from '../../../search/dtc-table-search/dtc-table-search.component';
import { DtcQuery } from '../../../../../shared/models/dtc/dtc-query';
import { SortingConfiguration } from '../../../../../shared/internal/sorting-configuration';
import { getPropertyName } from '../../../../common/common-utils';
import { Dtc } from '../../../../../shared/models/dtc/dtc';
import { FormsModule } from '@angular/forms';
import { ErrorMessageWithLinkedDtcs } from '../../../../../shared/models/error-messages/error-message-with-linked-dtcs';
import { ExclamationTriangleIconComponent } from '../../../icons/exclamation-triangle-icon/exclamation-triangle-icon.component';
import { DtcLink } from '../../../../../shared/models/dtc/dtc-link';
import { ScrollableBase } from '../../../base/scrollable-base';

@Component({
  selector: 'app-dtc-selection-table',
  imports: [HeaderSorterComponent, DtcTableSearchComponent, FormsModule, ExclamationTriangleIconComponent],
  templateUrl: './dtc-selection-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dtc-selection-table.component.scss',
})
export class DtcSelectionTableComponent extends ScrollableBase<DtcQuery> implements OnChanges {
  @Input() public errorMessageId?: string;
  @Input() public dtcs?: Dtc[];
  @Input() public isReadOnly = false;
  @Output() public searchTriggered: EventEmitter<DtcQuery> = new EventEmitter<DtcQuery>();
  @Output() public sortByFieldChanged: EventEmitter<DtcQuery> = new EventEmitter<DtcQuery>();
  @Output() public selectedDtcsChanged: EventEmitter<ErrorMessageWithLinkedDtcs> =
    new EventEmitter<ErrorMessageWithLinkedDtcs>();

  public getPropertyName = getPropertyName<Dtc>;
  public sortingConfiguration = new SortingConfiguration(this.getPropertyName('number'));
  public selectedDtcs = new Map<string, boolean>();
  public hoveredErrorMessageId?: string;
  public hoveredIndex?: number;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['dtcs'] && this.dtcs) {
      this.dtcs.forEach((dtc) => {
        if (!this.selectedDtcs.has(dtc.id!)) {
          this.selectedDtcs.set(dtc.id!, false);
        }
        if (dtc.errorMessageId === this.errorMessageId) {
          this.selectedDtcs.set(dtc.id!, true);
        }
      });
    }

    if (changes['errorMessageId'] && this.errorMessageId && this.dtcs) {
      this.dtcs.forEach((dtc) => {
        if (dtc.errorMessageId === this.errorMessageId) {
          this.selectedDtcs.set(dtc.id!, true);
        } else {
          this.selectedDtcs.set(dtc.id!, false);
        }
      });
    }
  }

  public onSearchTriggered(query: DtcQuery): void {
    this.query = query;
    const sortByField = this.sortingConfiguration.SortByField;
    if (sortByField) {
      this.query.orderBy = sortByField.field;
      this.query.orderByDescending = sortByField.orderByDescending;
    }
    this.searchTriggered.emit(query);
  }

  public sortBy(field: string): void {
    const sortByField = this.sortingConfiguration.sortBy(field);
    if (this.query) {
      this.query.orderBy = sortByField.field;
      this.query.orderByDescending = sortByField.orderByDescending;
      this.sortByFieldChanged.emit(this.query);
    }
  }

  public toggleSelection(dtcId: string): void {
    const isSelected = this.selectedDtcs.get(dtcId);
    if (isSelected !== undefined) {
      this.selectedDtcs.set(dtcId, !isSelected);
    }
    this.emitSelectedDtcs();
  }

  public toggleAllSelection(): void {
    const allSelected = Array.from(this.selectedDtcs.values()).every((selected) => selected);
    this.selectedDtcs.forEach((_, dtcId) => {
      if (this.dtcs?.some((dtc) => dtc.id === dtcId)) {
        this.selectedDtcs.set(dtcId, !allSelected);
      }
    });
    this.emitSelectedDtcs();
  }

  public isAllSelected(): boolean {
    return this.dtcs ? this.dtcs.every((dtc) => this.selectedDtcs.get(dtc.id!) === true) : false;
  }

  public showErrorMessage(id?: string, index?: number): void {
    this.hoveredErrorMessageId = id;
    this.hoveredIndex = index;
  }

  public hideErrorMessage(): void {
    this.hoveredErrorMessageId = undefined;
    this.hoveredIndex = undefined;
  }

  private emitSelectedDtcs(): void {
    const selectedDtcIds = Array.from(this.selectedDtcs.entries())
      .filter(([_, isSelected]) => isSelected)
      .map(([dtcId, _]) => dtcId);
    const unselectedDtcIds = Array.from(this.selectedDtcs.entries())
      .filter(([_, isSelected]) => !isSelected)
      .map(([dtcId, _]) => dtcId);

    const errorMessageWithLinkedDtcs: ErrorMessageWithLinkedDtcs = {
      linkedDtcs: this.buildDtcLinks(selectedDtcIds),
      unlinkedDtcs: this.buildDtcLinks(unselectedDtcIds),
    };

    this.selectedDtcsChanged.emit(errorMessageWithLinkedDtcs);
  }

  private getRowVersion(dtcId: string): number | undefined {
    return this.dtcs?.find((dtc) => dtc.id === dtcId)?.rowVersion;
  }

  private buildDtcLinks(dtcIds: string[]): DtcLink[] {
    return dtcIds.map((dtcId) => ({
      id: dtcId,
      rowVersion: this.getRowVersion(dtcId),
    }));
  }
}
